import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Card, Col, Row, Table, Spin, DatePicker, Flex, Statistic, Skeleton, Modal, Button, Typography, InputNumber, Tooltip } from "antd";
import { QuestionCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from "react-redux";
import Filter from "../../components/filter/Filter";
import { getDelegatesList } from "../../redux/slices/delegates/delegatesThunk";
import { getSamples } from "../../redux/slices/samples/samplesThunk";
import { getGivenSamplesSummary, getGivenSamplesAllKpis, getDelegateSamplePivotData, exportGivenSamplesSummary, exportDelegatePivot } from "../../redux/slices/givenSamples/givenSamplesThunk";
import { clearPivotData } from "../../redux/slices/givenSamples/givenSamplesSlice";
import { RoleEnum } from "../../_utils/roleEnum";
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { getRegions } from "../../redux/slices/regions/regionsThunk";

const { Text } = Typography;

const GivenSamplesSummaryPage = () => {
  const dispatch = useDispatch();
  
  const { user } = useSelector((state) => state.signin);
  const isSupervisor = user?.role?.id === RoleEnum.SUPERVISOR;
  const regionsList = useSelector((state) => state.regions?.regions?.data || []);
  const regions_loading = useSelector((state) => state.regions?.loading);

  const {
    summary: givenSamplesSummaryState,
    dashboardKpis: dashboardKpisState,
    delegatePivot: delegatePivotState
  } = useSelector((state) => state.givenSamples);
  
  const allDelegatesList = useSelector((state) => state.delegates.delegatesList?.data?.data || []);
  const delegates_loading = useSelector((state) => state.delegates.delegatesList?.loading);
  const rawSamplesList = useSelector((state) => state.samples.samples?.data || []);
  const samples_loading = useSelector((state) => state.samples.samples?.loading);

  const [paginationLocal, setPaginationLocal] = useState({ currentPage: 1, pageSize: 10 });
  const [pivotModalVisible, setPivotModalVisible] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPivot, setExportingPivot] = useState(false);

  const [filters, setFilters] = useState({
    delegateId: isSupervisor ? user?.supervisedDelegateIds : [],
    sampleId: [],
    personId: [],
    regionId: [],
    startDate: undefined,
    endDate: undefined,
    minQuantity: undefined,
    maxQuantity: undefined,
  });

  const mainTableMeta = useMemo(() => givenSamplesSummaryState?.meta, [givenSamplesSummaryState?.meta]);
  const mainTableData = useMemo(() => givenSamplesSummaryState?.data || [], [givenSamplesSummaryState?.data]);
  const mainTableLoading = givenSamplesSummaryState?.loading;
  
  const dashboardKpisData = dashboardKpisState?.data;
  const dashboardKpisLoading = dashboardKpisState?.loading;

  const givenSamplesTableColumns = useCallback((onDelegateClick) => [
    { title: "Code Échantillon", dataIndex: "sampleCode", key: "sampleCode", render: (text) => text || <Text type="secondary">-</Text>, sorter: (a, b) => String(a.sampleCode || '').localeCompare(String(b.sampleCode || '')), width: 150 },
    { title: "Échantillon", dataIndex: "sampleName", key: "sampleName", sorter: (a, b) => String(a.sampleName || '').localeCompare(String(b.sampleName || '')) },
    { title: "Qté", dataIndex: "quantityGiven", key: "quantityGiven", sorter: (a, b) => (a.quantityGiven || 0) - (b.quantityGiven || 0), width: 80, align: 'right' },
    { title: "Date Visite", dataIndex: "visitDate", key: "visitDate", render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : <Text type="secondary">N/A</Text>, sorter: (a, b) => dayjs(a.visitDate).valueOf() - dayjs(b.visitDate).valueOf(), width: 120 },
    { 
      title: "Région", 
      dataIndex: "regionName", 
      key: "regionName", 
      width: 150,
      render: (text) => text || <Text type="secondary">-</Text>,
      sorter: (a, b) => String(a.regionName || '').localeCompare(String(b.regionName || '')),
    },
    {
      title: "Délégué", key: "delegateName", width: 200, ellipsis: true,
      render: (record) => { const name = `${record.delegateFirstName || ''} ${record.delegateLastName || ''}`.trim(); return name ? (<Button type="link" onClick={() => onDelegateClick(record.delegateId, name)} style={{ padding: 0, height: 'auto', lineHeight: 'inherit' }}>{name}</Button>) : <Text type="secondary">N/A</Text>; },
      sorter: (a, b) => `${a.delegateFirstName || ''} ${a.delegateLastName || ''}`.localeCompare(`${b.delegateFirstName || ''} ${b.delegateLastName || ''}`),
    },
    {
      title: "Médecin/Contact", key: "personName", width: 200, ellipsis: true,
      render: (record) => { const name = `${record.personFirstName || ''} ${record.personLastName || ''}`.trim(); return name || <Text type="secondary">N/A</Text>; },
      sorter: (a, b) => `${a.personFirstName || ''} ${a.personLastName || ''}`.localeCompare(`${b.personFirstName || ''} ${b.personLastName || ''}`),
    },
  ], []);

  const pivotTableColumns = useMemo(() => [
    { title: "Code Échantillon", dataIndex: "sampleCode", key: "sampleCode", render: (text) => text || <Text type="secondary">-</Text> },
    { title: "Nom Échantillon", dataIndex: "sampleName", key: "sampleName" },
    { title: "Total Distribué", dataIndex: "totalQuantityDistributed", key: "totalQuantityDistributed", width: 150, sorter: (a, b) => (a.totalQuantityDistributed || 0) - (b.totalQuantityDistributed || 0), defaultSortOrder: 'descend', align: 'right' },
    {
      title: (<span>Médecins touchés <Tooltip title="Nombre de médecins uniques à qui cet échantillon a été donné par ce délégué."><QuestionCircleOutlined /></Tooltip></span>),
      dataIndex: "uniqueDoctorsForSample", key: "uniqueDoctorsForSample",
      sorter: (a, b) => (a.uniqueDoctorsForSample || 0) - (b.uniqueDoctorsForSample || 0),
      align: 'center', width: 150,
      render: (text) => typeof text === 'number' ? text : <Text type="secondary">-</Text>,
    },
    {
      title: (<span>Moy. Qté / Médecin <Tooltip title="Quantité totale de cet échantillon distribuée par ce délégué, divisée par le nombre de médecins uniques."><QuestionCircleOutlined /></Tooltip></span>),
      dataIndex: "averageQuantityPerDoctor", key: "averageQuantityPerDoctor",
      render: (text) => typeof text === 'number' ? text.toFixed(2) : <Text type="secondary">-</Text>,
      sorter: (a, b) => (a.averageQuantityPerDoctor || 0) - (b.averageQuantityPerDoctor || 0),
      align: 'right', width: 180,
    },
  ], []);
  
  useEffect(() => {
    dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000, filter: {} }));
    dispatch(getSamples({ page: 1, itemsPerPage: 1000, filter: {} }));
    dispatch(getRegions({ page: 1, itemsPerPage: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    const controller = new AbortController();
    const { page, limit, ...apiFilters } = filters;
    if (Array.isArray(apiFilters.delegateId)) {
      apiFilters.delegateId = apiFilters.delegateId.join(',');
    }
    if (Array.isArray(apiFilters.regionId)) { // <--- AJOUTÉ
      apiFilters.regionId = apiFilters.regionId.join(',');
    }

    dispatch(getGivenSamplesSummary({ page: paginationLocal.currentPage, limit: paginationLocal.pageSize, filters: apiFilters, signal: controller.signal }));
    return () => controller.abort();
  }, [dispatch, filters, paginationLocal.currentPage, paginationLocal.pageSize]);
  
  useEffect(() => {
    const controller = new AbortController();
    const { minQuantity, maxQuantity, ...kpiFilters } = filters;
    dispatch(getGivenSamplesAllKpis({ filters: kpiFilters, signal: controller.signal }));
    return () => controller.abort();
  }, [dispatch, filters]);

  const createOptions = (data, labelKeys = ["name"], idKey = "id") => {
    if (!Array.isArray(data)) return [];
    return data.map((item) => ({
      id: item[idKey],
      label: labelKeys.map(key => item[key]).filter(Boolean).join(' ').trim() || `ID: ${item[idKey]}`,
      value: item[idKey],
    }));
  };

  const delegateOptionsForFilter = useMemo(() => {
    let delegatesToDisplay = allDelegatesList;
    if (isSupervisor && user?.supervisedDelegateIds) {
      delegatesToDisplay = allDelegatesList.filter(d => user.supervisedDelegateIds.includes(d.id));
    }
    return createOptions(delegatesToDisplay, ["firstname", "lastname"]);
  }, [allDelegatesList, isSupervisor, user]);

  const sampleOptionsForFilter = useMemo(() => createOptions(rawSamplesList), [rawSamplesList]);
  
  const personOptionsForFilter = useMemo(() => {
    const uniquePersons = new Map();
    mainTableData.forEach(item => {
      if (item.personId) {
        uniquePersons.set(item.personId, { 
          id: item.personId, 
          value: item.personId, 
          label: `${item.personFirstName || ''} ${item.personLastName || ''}`.trim() 
        });
      }
    });
    return Array.from(uniquePersons.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [mainTableData]);
  
  const filterComponentOptionsLists = [
    { key: "delegateId", placeholder: "Délégué", multiple: true, isLoading: delegates_loading, options: delegateOptionsForFilter },
    { key: "regionId", placeholder: "Région", multiple: true, isLoading: regions_loading, options: createOptions(regionsList) },
    { key: "sampleId", placeholder: "Échantillon", multiple: true, isLoading: samples_loading, options: sampleOptionsForFilter },
    { key: "personId", placeholder: "Médecin", multiple: true, isLoading: mainTableLoading, options: personOptionsForFilter },
  ];

  const handleTableChange = (pagination) => {
    setPaginationLocal({ currentPage: pagination.current || 1, pageSize: pagination.pageSize || 10 });
  };

  const handleFilterChange = useCallback((value, key) => {
    setFilters(prev => ({...prev, [key]: value}));
    setPaginationLocal(p => ({ ...p, currentPage: 1 }));
  }, []);

  const handleDateRangeChange = useCallback((dates, dateStrings) => {
    setFilters(prev => ({ ...prev, startDate: dateStrings[0] || undefined, endDate: dateStrings[1] || undefined }));
    setPaginationLocal(p => ({ ...p, currentPage: 1 }));
  }, []);
  
  const handleQuantityChange = useCallback((value, type) => {
    setFilters(prev => ({...prev, [type === 'min' ? 'minQuantity' : 'maxQuantity']: value === null ? undefined : value }));
    setPaginationLocal(p => ({ ...p, currentPage: 1 }));
  }, []);

  const handleDelegateClickForPivot = useCallback((delegateId, delegateName) => {
    setPivotModalVisible(true);
    const { page, limit, delegateId: _dId, personId: _pId, ...relevantFiltersForPivot } = filters;
    dispatch(getDelegateSamplePivotData({ delegateId, filters: relevantFiltersForPivot, delegateInfo: { id: delegateId, name: delegateName } }));
  }, [dispatch, filters]);

  const closePivotModal = useCallback(() => {
    setPivotModalVisible(false);
    dispatch(clearPivotData());
  }, [dispatch]);

  const handleExport = () => {
    setExporting(true);
    const { page, limit, ...apiFilters } = filters;
    dispatch(exportGivenSamplesSummary(apiFilters))
      .unwrap()
      .then(() => toast.success("Le téléchargement va commencer."))
      .catch((err) => toast.error(`Erreur d'export: ${err}`))
      .finally(() => setExporting(false));
  };

  const handleExportPivot = () => {
    if (!delegatePivotState.currentDelegateInfo?.id) return;
    setExportingPivot(true);
    
    const { id: delegateId, name: delegateName } = delegatePivotState.currentDelegateInfo;
    const { page, limit, delegateId: _dId, personId: _pId, ...pivotFilters } = filters;

    dispatch(exportDelegatePivot({ delegateId, filters: pivotFilters, delegateName }))
      .unwrap()
      .then(() => toast.success("Le téléchargement du tableau croisé va commencer."))
      .catch((err) => toast.error(`Erreur d'export du tableau croisé: ${err}`))
      .finally(() => setExportingPivot(false));
  };
  
  return (
    <div className="tabled">
      <Row gutter={[16, 16]} style={{ marginBottom: '24px'}}>
        <Col xs={24} sm={12} md={8}><Card bordered={false}>{dashboardKpisLoading ? <Skeleton active paragraph={{ rows: 1 }} title={false} /> : <Statistic title="Total Échantillons Distribués" value={dashboardKpisData?.totalSamplesDistributed || 0} />}</Card></Col>
        <Col xs={24} sm={12} md={8}><Card bordered={false}>{dashboardKpisLoading ? <Skeleton active paragraph={{ rows: 1 }} title={false} /> : <Statistic title="Médecins Uniques Atteints" value={dashboardKpisData?.uniqueDoctorsReached || 0} />}</Card></Col>
        <Col xs={24} sm={12} md={8}><Card bordered={false}>{dashboardKpisLoading ? <Skeleton active paragraph={{ rows: 1 }} title={false} /> : <Statistic title="Délégués Ayant Distribué" value={dashboardKpisData?.uniqueDelegatesActive || 0} />}</Card></Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col xs={24} xl={24}>
          <Card bordered={false} className="criclebox tablespace mb-24" title="Résumé des Échantillons Donnés">
            
                <Flex wrap="wrap" gap="middle" align="center" className="custom-filters-container">
                    <Filter 
                        optionsLists={filterComponentOptionsLists} 
                        onSelect={handleFilterChange} 
                        data={filters}
                    />
                    <DatePicker.RangePicker
                        placeholder={['Date début', 'Date fin']}
                        onChange={handleDateRangeChange}
                        value={(filters.startDate && filters.endDate) ? [dayjs(filters.startDate), dayjs(filters.endDate)] : null}
                    />
                    <Text>Quantité:</Text>
                    <InputNumber placeholder="Min" min={0} value={filters.minQuantity} onChange={(v) => handleQuantityChange(v, 'min')} />
                    <Text>à</Text>
                    <InputNumber placeholder="Max" min={filters.minQuantity || 0} value={filters.maxQuantity} onChange={(v) => handleQuantityChange(v, 'max')} />
                     <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting}>Exporter CSV</Button>
                </Flex>
           
            <Table
                columns={givenSamplesTableColumns(handleDelegateClickForPivot)}
                dataSource={mainTableData}
                rowKey="reportSampleId"
                loading={mainTableLoading}
                pagination={{
                    current: paginationLocal.currentPage,
                    pageSize: paginationLocal.pageSize,
                    total: mainTableMeta?.totalItems,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} items`,
                }}
                onChange={handleTableChange}
                className="ant-border-space table-summmary"
              />
          </Card>
        </Col>
      </Row>
      {delegatePivotState.currentDelegateInfo && (
        <Modal 
          title={`Tableau Croisé pour Délégué: ${delegatePivotState.currentDelegateInfo.name}`} 
          open={pivotModalVisible} 
          onCancel={closePivotModal} 
          footer={[
            <Button key="export" icon={<DownloadOutlined />} onClick={handleExportPivot} loading={exportingPivot}>
              Exporter ce Tableau
            </Button>,
            <Button key="close" onClick={closePivotModal}>
              Fermer
            </Button>
          ]} 
          width={1000} 
          destroyOnClose 
        >
          {delegatePivotState.loading && <div style={{ textAlign: 'center', padding: '20px' }}><Spin tip="Chargement du pivot..." /></div>}
          {!delegatePivotState.loading && !delegatePivotState.error && delegatePivotState.data.length > 0 && (<Table columns={pivotTableColumns} dataSource={delegatePivotState.data} rowKey="sampleId" pagination={false} size="small" scroll={{ y: 300 }} /> )}
        </Modal>
      )}
    </div>
  );
};

export default GivenSamplesSummaryPage;